import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronRight, FiExternalLink } from 'react-icons/fi';
import { fetchFestivals } from '../../../services/api';
import AdminLayout, { AdminBanner } from '../AdminLayout/AdminLayout';
import { useMessages } from '../../../i18n/I18n';
import './FestivalsList.css';
import LoadingPlaceholder from '../../../components/LoadingPlaceholder';
import { FestivalExcerpt, festivalName } from '../../../utils/festivals';

const previewUrl = process.env.REACT_APP_PREVIEW_URL || '';
const apiUrl = process.env.REACT_APP_API_BASE_URL || '';

const festivalPreviewUrl = (festivalId: string) => {
  const parameters = new URLSearchParams({ festivalId, apiUrl });
  return `${previewUrl.replace(/\/$/, '')}/app.html?${parameters}`;
};

const FestivalsList: React.FC = () => {
  const t = useMessages('admin');
  const [masterKey, setMasterKey] = useState('');
  const [festivals, setFestivals] = useState<FestivalExcerpt[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    setHasSubmitted(true);

    try {
      const fetchedFestivals = await fetchFestivals(masterKey);
      setFestivals(fetchedFestivals);
    } catch (err) {
      setHasSubmitted(false);
      setError(err instanceof Error ? err.message : t.error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AdminLayout
      className="festivals-list-page"
      title={t.tools.festivalsList.title}
      description={t.festivalsListDescription}
      hasBackLink
    >
      {!hasSubmitted ? (
        <section className="admin-card">
          <form onSubmit={handleSubmit}>
            <div className="admin-field">
              <label htmlFor="masterKey">{t.masterKey}</label>
              <input
                type="password"
                id="masterKey"
                value={masterKey}
                onChange={(e) => setMasterKey(e.target.value)}
                required
              />
            </div>
            <div className="admin-actions">
              <button type="submit" className="btn btn-primary">{t.viewFestivals}</button>
            </div>
          </form>
        </section>
      ) : isLoading ? (
        <LoadingPlaceholder layout="table" count={4} />
      ) : festivals.length > 0 && (
        <div className="data-table festivals-table">
          <div className="data-table-scroll">
            <div className="data-table-head" aria-hidden="true">
              <span>{t.festivalId}</span>
              <span>{t.name}</span>
            </div>
            {festivals.map((festival) => (
              <div
                key={festival.festival_id}
                className="data-table-row festival-item"
              >
                <Link to={`/?festival_id=${festival.festival_id}`} className="festival-login">
                  <span className="festival-id">{festival.festival_id}</span>
                  <span className="festival-names">{festivalName(festival)}</span>
                  <FiChevronRight className="festival-chevron" aria-hidden="true" />
                </Link>
                {previewUrl && (
                  <a
                    href={festivalPreviewUrl(festival.festival_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn festival-preview"
                    aria-label={t.previewFestival(festivalName(festival))}
                  >
                    <FiExternalLink aria-hidden="true" />
                    {t.preview}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {error && <AdminBanner tone="danger">{error}</AdminBanner>}
    </AdminLayout>
  );
};

export default FestivalsList;
